import { useState } from 'react';
import { TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, BG_SURFACE, BRAND_PRIMARY, FONT_BUTTON, FONT_LABEL, FONT_SMALL, FONT_TINY } from '../../theme';
import { levelFromScore, LEVEL_COLORS } from './RiskForm';

// ─── I18N ─────────────────────────────────────────────────────────────────────
const T = {
  es: {
    showMatrix:  'Mostrar matriz de riesgo',
    hideMatrix:  'Ocultar matriz de riesgo',
    likelihood:  'Probabilidad',
    impact:      'Impacto',
    low:         'Bajo',
    medium:      'Medio',
    high:        'Alto',
    liLabels:    ['Improbable', 'Poco probable', 'Probable', 'Muy probable', 'Casi seguro'],
    imLabels:    ['Insignificante', 'Leve', 'Moderado', 'Alto', 'Muy alto'],
  },
  en: {
    showMatrix:  'Show risk matrix',
    hideMatrix:  'Hide risk matrix',
    likelihood:  'Likelihood',
    impact:      'Impact',
    low:         'Low',
    medium:      'Medium',
    high:        'High',
    liLabels:    ['Improbable', 'Unlikely', 'Likely', 'Very Likely', 'Almost Certain'],
    imLabels:    ['Negligible', 'Slight', 'Moderate', 'High', 'Very High'],
  },
};

// ─── LAYOUT CONSTANTS ─────────────────────────────────────────────────────────
const CELL = 60;  // cell width & height in px
const GAP  = 2;   // gap between cells in px
const AXIS = 36;  // width of the rotated Impact axis column in px
const ROW  = 20;  // width reserved for row-number labels in px

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function RiskMatrix({ lang, thresholds }) {
  const [open, setOpen] = useState(false);
  const t = T[lang] || T.en;

  // Effective thresholds for the legend ranges. Mirrors the defaults used by
  // the canonical levelFromScore (low=4, medium=9) when no org config is passed.
  const { low = 4, medium = 9 } = thresholds || {};

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {/* Toggle */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          ...FONT_BUTTON,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          color: BRAND_PRIMARY,
        }}
      >
        {open ? `▾ ${t.hideMatrix}` : `▸ ${t.showMatrix}`}
      </button>

      {/* Matrix body */}
      {open && (
        <div style={{
          background: BG_SURFACE,
          border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 10,
          padding: '1rem',
          marginTop: '0.75rem',
          display: 'inline-block',
        }}>
          {/* Likelihood axis label */}
          <div style={{
            ...FONT_SMALL,
            color: TEXT_MUTED,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: '0.25rem',
            marginLeft: AXIS + ROW,
          }}>
            {t.likelihood} →
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            {/* Impact axis label (rotated) */}
            <div style={{
              width: AXIS,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingBottom: 20,
            }}>
              <span style={{
                ...FONT_SMALL,
                color: TEXT_MUTED,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                transform: 'rotate(-90deg)',
                whiteSpace: 'nowrap',
                display: 'block',
              }}>
                ↑ {t.impact}
              </span>
            </div>

            {/* Grid cells area */}
            <div>
              {/* Column headers (Likelihood 1–5) */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(5, ${CELL}px)`,
                gap: GAP,
                marginBottom: 2,
                marginLeft: ROW,
              }}>
                {[1, 2, 3, 4, 5].map((l) => (
                  <div key={l} style={{
                    ...FONT_SMALL,
                    textAlign: 'center',
                    color: TEXT_MUTED,
                    paddingBottom: '0.2rem',
                  }}>
                    {l}
                  </div>
                ))}
              </div>

              {/* Rows: impact 5 → 1 (top to bottom) */}
              {[5, 4, 3, 2, 1].map((imp) => (
                <div key={imp} style={{ display: 'flex', alignItems: 'center', marginBottom: GAP }}>
                  {/* Row label */}
                  <div style={{
                    ...FONT_SMALL,
                    width: ROW,
                    flexShrink: 0,
                    color: TEXT_MUTED,
                    textAlign: 'right',
                    paddingRight: 4,
                  }}>
                    {imp}
                  </div>
                  {/* Cells */}
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(5, ${CELL}px)`, gap: GAP }}>
                    {[1, 2, 3, 4, 5].map((lik) => {
                      const score = lik * imp;
                      const c = LEVEL_COLORS[levelFromScore(score, thresholds)];
                      return (
                        <div key={lik} style={{
                          ...FONT_LABEL,
                          width: CELL,
                          height: CELL,
                          background: c.bg,
                          borderRadius: 3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          color: c.text,
                        }}>
                          {score}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Likelihood labels */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(5, ${CELL}px)`,
                gap: GAP,
                marginTop: 4,
                marginLeft: ROW,
              }}>
                {t.liLabels.map((lbl, i) => (
                  <div key={i} style={{
                    ...FONT_TINY,
                    color: TEXT_DISABLED,
                    textAlign: 'center',
                    lineHeight: 1.2,
                    padding: '0 1px',
                  }}>
                    {lbl}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Legend — inline row */}
          <div style={{
            display: 'flex',
            gap: '1.25rem',
            marginTop: '0.5rem',
            marginLeft: AXIS + ROW,
            alignItems: 'center',
          }}>
            {[
              { ...LEVEL_COLORS.LOW,    label: `${t.low} 1–${low}` },
              { ...LEVEL_COLORS.MEDIUM, label: `${t.medium} ${low + 1}–${medium}` },
              { ...LEVEL_COLORS.HIGH,   label: `${t.high} ${medium + 1}–25` },
            ].map(({ bg, text, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <div style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  background: bg,
                  flexShrink: 0,
                }} />
                <span style={{
                  ...FONT_LABEL,
                  color: text,
                  whiteSpace: 'nowrap',
                }}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
